/* Copyright (c) 2018-2025, NVIDIA CORPORATION.  All rights reserved.
 *
 * NVIDIA CORPORATION and its licensors retain all intellectual property
 * and proprietary rights in and to this software, related documentation
 * and any modifications thereto.  Any use, reproduction, disclosure or
 * distribution of this software and related documentation without an express
 * license agreement from NVIDIA CORPORATION is strictly prohibited.
 */

import { Injectable } from '@angular/core';
import { map } from 'rxjs/operators';
import { Commands } from 'bridge-commands/commands';
import { BridgeService } from '../bridge/bridge.service';
import { EventHandler } from '../events-handler/events-handler';
import { EventsHandlerService } from '../events-handler/events-handler.service';
import { LoggingService, Logger } from '../logging/logging.service';
import { NativeEvents, STRUCTURED_JSON_PREFIX, NativeEventFactory } from './native-events';

@Injectable({
    providedIn: 'root'
})
export class NativeEventService extends EventsHandlerService<NativeEvents, string, string> {
    private logger: Logger;

    constructor(bridgeService: BridgeService, loggingService: LoggingService) {
        const windowEventLogger = loggingService.getLogger('nativeEventService');
        super(bridgeService, windowEventLogger);
        this.logger = windowEventLogger;
        this.defaultHandlerType = EventHandler.Types.HandlerEventType.SimpleWindowEvent;
        this.registerNewHandler(
            EventHandler.Types.HandlerEventType.StructuredWindowEvent,
            (content: string) => content.startsWith(STRUCTURED_JSON_PREFIX),
            (content: string) => {
                const obj = JSON.parse(content);
                return obj.type as NativeEvents;
            },
            (content: string) => content,
            (content: string) => content.substring(STRUCTURED_JSON_PREFIX.length)
        );
        this.registerNewHandler(
            EventHandler.Types.HandlerEventType.SimpleWindowEvent,
            (content: string) => !content.startsWith(STRUCTURED_JSON_PREFIX),
            (content: string) => content as NativeEvents,
            (content: string) => content,
            (content: string) => content
        );

        this.bridgeService
            .executeCommand(Commands.Common.Windowevents.RegisterWindowEventsCallback, {})
            .subscribe((response: string) => {
                this.processMessage(response);
            });
    }

    getNativeEvent$(key: NativeEvents) {
        return this.get$(key, EventHandler.Types.HandlerEventType.StructuredWindowEvent).pipe(
            map(content => NativeEventFactory.create(JSON.parse(content)))
        );
    }
}
